import { createContext } from "react";
import { formatISO } from "date-fns";

import ActionTypes from "./action-types";
import URLS from "./urls";
import { diffArraysByKey } from "./lib/array";

export const fetchInitialValues = {
  count: 0,
  next: null,
  previous: null,
  results: []
};

const initialSevenRooms = Object.freeze({
  loaded: false,
  event_blocks: fetchInitialValues,
  venues: {
    status: 0,
    data: {
      results: []
    }
  },
  venue_availability: {
    status: 0,
    data: {
      availability: []
    }
  },
  reservation_requests: {
    status: 0,
    data: {
      results: []
    }
  },
  reservations: {
    status: 0,
    data: {
      results: []
    }
  },
  reservation_created: false
});

export const initialState = Object.freeze({
  appointment: null,
  appointments: fetchInitialValues,
  appointments_setting: fetchInitialValues,
  dates: fetchInitialValues,
  errors: null,
  group: {
    group_name: null
  },
  group_members: { group_id: null, members: [] },
  interval: null,
  intervals: fetchInitialValues,
  liability: false,
  loading: false,
  optins: null,
  optinInd: null,
  people: fetchInitialValues,
  person: {},
  photo: null,
  photos: fetchInitialValues,
  photosToReview: fetchInitialValues,
  reciprocity: false,
  setting: {
    email: null,
    updated: null
  },
  slots: fetchInitialValues,
  updated: false,
  sms: fetchInitialValues,
  smsTemplates: fetchInitialValues,
  smsRenderedTemplate: { data: "" },
  smsNotifications: [],
  votingRate: fetchInitialValues,
  votingRateOthers: fetchInitialValues,
  verifyRequired: fetchInitialValues,
  pass_status: {
    loaded: false,
    pass_status: null,
    pass_status_secondary: null
  },
  sevenrooms: initialSevenRooms,
  balance: {
    balance: [],
    balance_sum: 0
  },
  main_ledger: {
    limit: 25,
    main_ledger: [],
    offset: 0,
    person: null,
    powered_only_ind: false,
    revenue_categories: {},
    total_balance: 0,
    with_balance: false,
  },
  personal_assistants: fetchInitialValues,
  refund_requests: fetchInitialValues,
  applications: {
    ...fetchInitialValues,
    results: [
      {
        submission: {
          personal_information: {},
          partner_membership: {},
          questions: {},
          battery_powered_membership: {},
          gym: {}
        },
        battery_powered_types: [],
        open_houses: [],
        requires_battery_powered: null,
        person: null
      }
    ],
    updated: false,
    open_houses: fetchInitialValues,
    open_house_rsvps: fetchInitialValues,
    open_house: {}
  },
  choosePassword: {},
  membershipchanges: {
    changes: { ...fetchInitialValues, person: { first_name: "", last_name: "", algilysys_id: "" } },
    options: { options: [], prices: [] }
  },
  previewCharges: {}
});

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.LOADING:
      return { ...state, loading: true };
    case ActionTypes.DATES:
      return { ...state, loading: false, dates: { ...action.data } };
    case ActionTypes.SLOTS:
      return { ...state, loading: false, slots: { ...action.data } };
    case ActionTypes.SELECT_DATE:
      return { ...state, loading: false, slots: { ...action.data } };
    case ActionTypes.OPTINS:
      return { ...state, loading: false, optins: { ...action.data } };
    case ActionTypes.APPOINTMENTS:
      return { ...state, loading: false, appointments: { ...action.data } };
    case ActionTypes.APPOINTMENT:
      return { ...state, loading: false, appointment: { ...action.data } };
    case ActionTypes.POST_APPOINTMENT:
      return { ...state, loading: false, appointment: { ...action.data } };
    case ActionTypes.PATCH_APPOINTMENT:
      return {
        ...state,
        loading: false,
        updated: true,
        appointment: {
          ...state.appointment,
          special_request_tx: action.data.special_request_tx
        }
      };
    case ActionTypes.POST_OPTINS:
      return {
        ...state,
        loading: false,
        optins: { ...fetchInitialValues, count: 1, results: [action.data] }
      };
    case ActionTypes.PERSON:
      return { ...state, loading: false, person: { ...action.data } };
    case ActionTypes.PERSON_APPOINTMENTS:
      return { ...state, loading: false, appointments: { ...action.data } };
    case ActionTypes.SETTING:
      return {
        ...state,
        loading: false,
        setting: { updated: null, ...action.data }
      };
    case ActionTypes.SETTINGS:
      return { ...state, loading: false, settings: { ...action.data } };
    case ActionTypes.APPOINTMENTS_SETTING:
      return { ...state, loading: false, updated: false, appointments_setting: { ...action.data } };
    case ActionTypes.PUT_APPOINTMENTS_SETTING:
      return { ...state, loading: false, updated: true, appointment_setting: { ...action.data } };
    case ActionTypes.ERROR:
      return { ...state, loading: false, errors: action.errors };
    case ActionTypes.ACCEPT_LIABILITY:
      return { ...state, liability: true };
    case ActionTypes.DECLINE_LIABILITY:
      return { ...state, liability: false };
    case ActionTypes.SEARCH_PEOPLE:
      return { ...state, loading: false, people: { ...action.data } };
    case ActionTypes.SELECT_PERSON:
      return {
        ...state,
        loading: false,
        people: fetchInitialValues,
        person: action.person
      };
    case ActionTypes.UNSELECT_PERSON:
      return { ...state, loading: false, person: {} };
    case ActionTypes.DELETE_APPOINTMENT:
      const appointments = state.appointments.results.map((appt) => {
        const apptUrl = `${URLS.APPOINTMENTS_API_LIST}${appt.id}/`;
        if (apptUrl === action.urlString) {
          return { ...appt, deleted_dttm: formatISO(new Date()) };
        }
        return appt;
      });
      return {
        ...state,
        loading: false,
        appointments: { ...state.appointments, results: appointments }
      };
    case ActionTypes.DELETE_OPTIN:
      return { ...state, loading: false, optinInd: false };
    case ActionTypes.CHANGE_OPTIN:
      return { ...state, loading: false, optinInd: true };
    case ActionTypes.INTERVALS:
      return { ...state, loading: false, intervals: { ...action.data } };
    case ActionTypes.INTERVAL:
      return { ...state, loading: false, interval: { ...action.data } };
    case ActionTypes.POST_RECIPROCITY:
      return { ...state, loading: false, reciprocity: true };
    case ActionTypes.GROUP_MEMBERS_ADMIN:
      return { ...state, loading: false, group_members: { ...action.data } };
    case ActionTypes.GROUP_DETAIL:
      return { ...state, loading: false, group: { ...action.data } };
    case ActionTypes.GROUP_MEMBERS_ADMIN_RESTORE:
      return { ...state, loading: false };
    case ActionTypes.PHOTOS:
      return { ...state, loading: false, photos: action.data };
    case ActionTypes.PHOTOS_TO_REVIEW:
      return { ...state, loading: false, photosToReview: action.data };
    case ActionTypes.SMS_API_LIST:
      return { ...state, loading: false, sms: action.data };
    case ActionTypes.SMS_API_LIST_REFRESH:
      const diff =
        state.sms.results.length > 0
          ? diffArraysByKey(state.sms.results, "pk", action.data.results, "pk")
          : [];
      return { ...state, loading: false, sms: action.data, smsNotifications: diff };
    case ActionTypes.SMS_API_TEMPLATE_LIST:
      return { ...state, loading: false, smsTemplates: action.data };
    case ActionTypes.SMS_ADMIN_TEMPLATE_RENDER_SMS:
      return { ...state, loading: false, smsRenderedTemplate: action.data };
    case ActionTypes.SMS_ADMIN_TEMPLATE_RENDER_SMS_CLEAR:
      return { ...state, loading: false, smsRenderedTemplate: { data: "" } };
    case ActionTypes.VOTING_API_RATE_MY:
      return { ...state, loading: false, votingRate: action.data };
    case ActionTypes.VOTING_API_RATE_OTHERS:
      return { ...state, loading: false, votingRateOthers: action.data };
    case ActionTypes.VERIFY_REQUIRED_API:
      return { ...state, loading: false, verifyRequired: action.data };
    case ActionTypes.PASS_STATUS_API_DETAIL:
      return { ...state, loading: false, pass_status: { loaded: true, ...action.data } };
    case ActionTypes.SEVENROOMS_API_ADMIN_RESERVATIONS:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, reservations: action.data }
      };
    case ActionTypes.SEVENROOMS_API_ADMIN_RESERVATIONS_TODAY:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, reservations: action.data }
      };
    case ActionTypes.SEVENROOMS_API_RESERVATIONS:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, reservations: action.data }
      };
    case ActionTypes.SEVENROOMS_API_RESERVATIONS_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        errors: false,
        sevenrooms: { ...state.sevenrooms, loaded: true, reservation_created: true }
      };
    case ActionTypes.SEVENROOMS_API_RESERVATION_REQUESTS:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, reservation_requests: action.data }
      };
    case ActionTypes.SEVENROOMS_API_VENUE_AVAILABILITY:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, venue_availability: action.data }
      };
    case ActionTypes.SEVENROOMS_API_VENUES:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, venues: action.data }
      };
    case ActionTypes.SEVENROOMS_API_EVENT_BLOCKS:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, event_blocks: action.data }
      };
    case ActionTypes.SEVENROOMS_API_EVENT_BLOCKS_DETAIL_DELETE:
      return {
        ...state,
        loading: false,
        errors: {},
        sevenrooms: { ...state.sevenrooms, loaded: true, event_blocks: fetchInitialValues }
      };
    case ActionTypes.SEVENROOMS_ERROR:
      return { ...state, loading: false, errors: action.errors };
    case ActionTypes.FINANCE_API_BALANCE:
      return {
        ...state,
        loading: false,
        balance: action.data
      };
    case ActionTypes.FINANCE_API_BALANCE_DETAILS:
      const balance = state.balance.balance.map((entry) => {
        if (entry.synthkey === action.data.synthkey) {
          return { ...entry, revenue: action.data.revenue };
        }
        return entry;
      });
      return {
        ...state,
        loading: false,
        balance: {
          balance,
          balance_sum: state.balance.balance_sum
        }
      };
    case ActionTypes.FINANCE_API_MAIN_LEDGER:
      return { ...state, loading: false, main_ledger: action.data };
    case ActionTypes.PERSONAL_ASSISTANTS_API_LIST:
      return { ...state, loading: false, personal_assistants: action.data };
    case ActionTypes.FINANCE_API_REFUND_REQUESTS:
      return { ...state, loading: false, refund_requests: action.data };
    case ActionTypes.APPLICATIONS_MEMBER:
      return { ...state, loading: false, applications: { ...state.applications, ...action.data } };
    case ActionTypes.APPLICATIONS_MEMBER_CHOOSE_PASSWORD_POST:
      return { ...state, loading: false, choosePassword: action.data };
    case ActionTypes.APPLICATIONS_MEMBER_POST:
      return {
        ...state,
        applications: { ...state.applications, results: [action.data] }
      };
    case ActionTypes.APPLICATIONS_MEMBER_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, updated: true }
      };
    case ActionTypes.APPLICATIONS_ADMIN_OPEN_HOUSE:
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, open_houses: action.data }
      };
    case ActionTypes.APPLICATIONS_ADMIN:
      return { ...state, loading: false, applications: { ...state.applications, ...action.data } };
    case ActionTypes.APPLICATIONS_ADMIN_DETAIL:
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, application: action.data }
      };
    case ActionTypes.APPLICATIONS_ADMIN_DETAIL_PATCH:
      const updated = state.applications.results.map((n) => {
        if (n.id === action.data.id) {
          return action.data;
        }
        return n;
      });
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, results: updated }
      };
    case ActionTypes.APPLICATIONS_ADMIN_OPEN_HOUSE_ADD_SUCCESS:
      return {
        ...state,
        loading: false,
        applications: {
          ...state.applications,
          open_houses: { ...state.applications.open_houses, updated: true }
        }
      };
    case ActionTypes.APPLICATIONS_ADMIN_OPEN_HOUSE_DETAIL:
      return {
        ...state,
        loading: false,
        applications: {
          ...state.applications,
          open_house: action.data
        }
      };
    case ActionTypes.APPLICATIONS_ADMIN_OPEN_HOUSE_DETAIL_DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        applications: {
          ...state.applications,
          open_houses: { ...state.applications.open_houses, deleted: true }
        }
      };
    case ActionTypes.APPLICATIONS_MEMBER_OPEN_HOUSE:
      return {
        ...state,
        loading: false,
        applications: {
          ...state.applications,
          open_houses: action.data
        }
      };
    case ActionTypes.APPLICATIONS_MEMBER_OPEN_HOUSE_RSVP:
      return {
        ...state,
        loading: false,
        applications: {
          ...state.applications,
          open_house_rsvps: action.data
        }
      };
    case ActionTypes.APPLICATIONS_MEMBER_OPEN_HOUSE_RSVP_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, created: true, deleted: false }
      };
    case ActionTypes.APPLICATIONS_MEMBER_OPEN_HOUSE_RSVP_DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        applications: { ...state.applications, deleted: true, created: false }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_LIST:
      return {
        ...state,
        loading: false,
        membershipchanges: { ...state.membershipchanges, changes: action.data }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_ACCOUNT_LIST:
      return {
        ...state,
        loading: false,
        membershipchanges: { ...state.membershipchanges, changes: action.data }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_LIST_DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        membershipchanges: {
          ...state.membershipchanges,
          changes: {
            ...fetchInitialValues,
            person: state.membershipchanges.changes.person,
            deleted: true,
            created: false
          }
        }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_LIST_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        membershipchanges: {
          ...state.membershipchanges,
          changes: {
            ...fetchInitialValues,
            created: true,
            deleted: false,
            person: state.membershipchanges.changes.person
          }
        }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_OPTIONS_LIST:
      return {
        ...state,
        loading: false,
        membershipchanges: { ...state.membershipchanges, options: action.data }
      };
    case ActionTypes.MEMBERSHIP_CHARGES_LIST:
      return {
        ...state,
        loading: false,
        previewCharges: {
          excluded: { ...state.previewCharges.excluded },
          mismatch: { ...state.previewCharges.mismatch },
          included: action.data
        }
      };
    case ActionTypes.MEMBERSHIP_CHARGES_EXCLUDED_LIST:
      return {
        ...state,
        loading: false,
        previewCharges: {
          included: { ...state.previewCharges.accounts },
          mismatch: { ...state.previewCharges.mismatch },
          excluded: action.data
        }
      };
    case ActionTypes.MEMBERSHIP_CHARGES_MISMATCH_LIST:
      return {
        ...state,
        loading: false,
        previewCharges: {
          included: { ...state.previewCharges.accounts },
          excluded: { ...state.previewCharges.excluded },
          mismatch: action.data
        }
      };
    case ActionTypes.MEMBERSHIP_CHANGES_UPLOAD_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        membershipchanges: { ...state.membershipchanges, uploaded: true }
      };
    default:
      return state;
  }
};

const Context = createContext(null);

export default Context;
